import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Modal,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import AddIcon from '@mui/icons-material/Add';
import { Close, CreateOutlined, DeleteOutline } from '@mui/icons-material';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  changeUseMylist,
  copyMylist,
  createMylist,
  deleteMylist,
  getMylists,
  updateMylist
} from '../../../api/db/mylist';
import {
  gray10,
  gray100,
  gray70,
  gray90,
  primary10,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor,
  primaryColor,
  tertiaryButtonOutlinedColor,
  white
} from '../../../constants/Colors';
import { sideBarWidth } from '../../../constants/Location';
import { sentryLog } from '../../../config/sentry';
import {
  MylistFormType,
  MylistType
} from '../../../validation/mylist/mylistType';
import CustomPagination from '../../uiParts/CustomPagination';
import CustomTextfield from '../../uiParts/CustomTextfield';
import SideBar from '../../uiParts/SideBar';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomHead from '../CustomHead';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import { AxiosError } from 'axios';
import { response } from '../../../api/db';
import { common } from '../../../modules/error';
import { yupResolver } from '@hookform/resolvers/yup';
import { mylistValidationSchema } from '../../../validation/mylist';
import { AuthContext } from '../../../contexts/auth';

const styles = {
  pageWrapper: {
    width: '100%',
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column'
    }
  },
  wrapper: {
    ml: sideBarWidth,
    width: '100%',
    '@media screen and (max-width: 960px)': {
      ml: '0'
    }
  },
  contentHeader: {
    alignItems: 'center',
    justifyContent: 'space-between',
    p: '32px 32px 24px',
    '@media screen and (max-width: 600px)': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: '24px 16px 20px 16px'
    }
  },
  title: {
    '@media screen and (max-width: 600px)': {
      fontSize: '20px'
    },
    color: gray100,
    fontSize: '24px',
    fontWeight: 700
  },
  headerTitle: {
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    '@media screen and (max-width: 960px)': {
      fontSize: '24px'
    }
  },
  headerLeft: {
    width: 'auto',
    '@media screen and (max-width: 600px)': {
      alignItems: 'center',
      justifyContent: 'space-between'
    }
  },
  headerRight: {
    columnGap: '16px',
    flexDirection: 'row',
    width: 'auto',
    '@media screen and (max-width: 600px)': {
      '&.MuiGrid-container div': {
        width: '100%'
      }
    }
  },
  materialList: {
    p: '0 32px 32px',
    '@media screen and (max-width: 600px)': {
      p: '0 16px 16px'
    }
  },
  cardContent: {
    columnGap: '24px',
    display: 'flex',
    flexDirection: 'row',
    padding: '0px',
    '@media screen and (max-width: 600px)': {
      columnGap: '0'
    },
    '&:last-child': {
      paddingBottom: 0
    }
  },
  editButton: {
    boxSizing: 'border-box',
    height: '44px',
    mr: '16px',
    p: '10px 22px 10px 16px',
    '@media screen and (max-width: 600px)': {
      flexGrow: '1'
    }
  },
  createButton: {
    boxSizing: 'border-box',
    height: '44px',
    p: '10px 22px 10px 16px',
    fontSize: '16px',
    ...primaryButtonBackgroundColor
  },
  copyButton: {
    boxSizing: 'border-box',
    height: '44px',
    mr: '16px',
    p: '10px 22px 10px 16px',
    '@media screen and (max-width: 600px)': {
      flexGrow: '1'
    }
  },
  deleteButton: {
    boxSizing: 'border-box',
    height: '44px',
    p: '10px 22px 10px 16px',
    '@media screen and (max-width: 600px)': {
      display: 'none'
    }
  },
  deleteIcon: {
    color: gray90,
    display: 'none',
    fontSize: '32px',
    '&:hover': {
      opacity: '0.8'
    },
    '@media screen and (max-width: 600px)': {
      display: 'block'
    }
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    maxWidth: '512px',
    minWidth: '294px',
    bgcolor: 'white',
    p: '24px'
  },
  pagination: {
    '& .MuiPagination-ul': {
      justifyContent: 'center'
    },
    '& .MuiButtonBase-root.MuiPaginationItem-root': {
      '&.Mui-selected': {
        backgroundColor: primaryColor,
        color: white
      }
    },
    color: gray100
  },
  paginationWrapper: {
    m: '80px auto'
  },
  table: {
    border: `1px solid ${gray70}`,
    borderRight: 'none'
  },
  row: {
    borderBottom: `1px solid ${gray70}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: '37px'
  },
  item: {
    bgcolor: gray10,
    height: '100%',
    padding: '8px 16px'
  },
  cell: {
    borderRight: `1px solid ${gray70}`,
    height: '100%',
    padding: '8px 12px'
  },
  cellText: {
    '@media screen and (max-width: 600px)': {
      fontSize: '12px'
    },
    color: gray100,
    fontSize: '14px'
  }
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50%',
  maxWidth: '512px',
  minWidth: '294px',
  bgcolor: 'white',
  p: '24px'
};

const MyListList = (): React.ReactElement => {
  const [loading, setLoading] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [infoText, setInfoText] = useState('');
  const navigate = useNavigate();
  const { checkLoginStatus } = useContext(AuthContext);

  // 画面アクセス制御
  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (!checkLoginStatus) return navigate('/auth/login');
      const result = await checkLoginStatus();
      if (!result.check) {
        navigate('/auth/login');
        return;
      }
    })();
  }, [checkLoginStatus]); // 画面アクセス制御

  // エラーハンドリング
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = (error: any) => {
    sentryLog(error);
    const axiosError = error as AxiosError;
    if (axiosError.isAxiosError && axiosError.response) {
      const loginErrorResponse = axiosError.response as response;
      setErrorText(loginErrorResponse.data.message);
      setSnackbarOpen(true);
    } else {
      setErrorText(common);
      setSnackbarOpen(true);
    }
  };

  const [showList, setShowList] = useState<boolean>(false);
  const [lists, setLists] = useState<MylistType[]>([
    {
      id: 1,
      company_id: 1,
      title: '',
      remarks_first: '',
      remarks_second: '',
      user_id: 1,
      use_flg: false,
      updated_by: '',
      product_count: 0,
      created_at: '',
      updated_at: '',
      deleted_at: ''
    }
  ]);

  const convertDateFormat = (dateString: string): string => {
    return dayjs(dateString).format('YYYY/MM/DD');
  };

  // ページネーション
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    topRef?.current?.scrollIntoView();
  };
  const topRef = useRef<HTMLDivElement>(null);

  const fetchMylists = async () => {
    const limit = 20;
    const offset = (currentPage - 1) * limit;
    const res = await getMylists(limit, offset);
    setLists(res.mylists);
    const totalPages = Math.ceil(res.total_count / limit);
    setTotalPages(totalPages);
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        clearSnackbar();
        await fetchMylists();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        handleError(error);
      } finally {
        setLoading(false);
        setShowList(true);
      }
    })();
  }, [currentPage]);

  // マイリストモーダルフォームの初期値
  const {
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<MylistFormType>({
    resolver: yupResolver<MylistFormType>(mylistValidationSchema),
    defaultValues: {
      title: '',
      remarks1: '',
      remarks2: ''
    }
  });

  // モーダル表示
  const [focusMylist, setFocusMylist] = React.useState<MylistFormType | null>(
    null
  );
  const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = React.useState<boolean>(false);
  const [openCopyModal, setOpenCopyModal] = React.useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = React.useState<boolean>(false);

  const handleOpenDeleteModal = (): void => {
    setOpenDeleteModal(true);
  };

  const handleOpenEditModal = ({
    title,
    remarks1,
    remarks2
  }: MylistFormType): void => {
    setValue('title', title);
    setValue('remarks1', remarks1);
    setValue('remarks2', remarks2);
    setOpenEditModal(true);
  };

  const handleOpenCopyModal = ({
    title,
    remarks1,
    remarks2
  }: MylistFormType): void => {
    setValue('title', title);
    setValue('remarks1', remarks1);
    setValue('remarks2', remarks2);
    setOpenCopyModal(true);
  };

  const handleOpenCreateModal = (): void => {
    setOpenCreateModal(true);
  };

  useEffect(() => {
    if (!focusMylist) reset();
  }, [focusMylist]);

  const clearSnackbar = () => {
    setInfoText('');
    setErrorText('');
  };

  // マイリスト作成
  const handleCreateMylist = () => {
    void (async () => {
      try {
        setLoading(true);
        clearSnackbar();
        await createMylist(watch);
        await fetchMylists();
        setSnackbarOpen(true);
        setInfoText('マイリストを作成しました');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        handleError(error);
      } finally {
        setOpenCreateModal(false);
        setLoading(false);
      }
    })();
  };

  // マイリスト編集
  const handleEditMylist = () => {
    void (async () => {
      try {
        setLoading(true);
        clearSnackbar();
        if (focusMylist?.id) {
          await updateMylist(focusMylist?.id, watch);
          await fetchMylists();
          setSnackbarOpen(true);
          setInfoText('マイリストを編集しました');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        handleError(error);
      } finally {
        setOpenEditModal(false);
        setLoading(false);
        setFocusMylist(null);
      }
    })();
  };

  // コピーで作成
  const handleCopyMylist = () => {
    void (async () => {
      try {
        setLoading(true);
        clearSnackbar();
        if (focusMylist?.id) {
          await copyMylist(focusMylist?.id, watch);
          await fetchMylists();
          setSnackbarOpen(true);
          setInfoText('マイリストをコピーしました');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        handleError(error);
      } finally {
        setOpenCopyModal(false);
        setLoading(false);
        setFocusMylist(null);
      }
    })();
  };

  // マイリスト削除
  const handleDeleteMylist = () => {
    void (async () => {
      try {
        setLoading(true);
        clearSnackbar();
        if (focusMylist?.id) {
          await deleteMylist(focusMylist?.id);
          await fetchMylists();
          setSnackbarOpen(true);
          setInfoText('マイリストを削除しました');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        handleError(error);
      } finally {
        setOpenDeleteModal(false);
        setLoading(false);
        setFocusMylist(null);
      }
    })();
  };

  // 利用中のマイリスト変更
  const handleChangeUseMylist = (e: React.ChangeEvent<HTMLInputElement>) => {
    void (async () => {
      try {
        setLoading(true);
        clearSnackbar();
        await changeUseMylist(Number(e.target.value));
        await fetchMylists();
        setSnackbarOpen(true);
        setInfoText('利用するマイリストを変更しました');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        handleError(error);
      } finally {
        setLoading(false);
        setFocusMylist(null);
      }
    })();
  };

  const [firstSubmit, setFirstSubmit] = useState(false);
  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('title') === undefined ||
        '' ||
        Boolean(errors.title) ||
        Boolean(errors.remarks1) ||
        Boolean(errors.remarks2)
      );
    }
    return false;
  };

  return (
    <>
      <CustomHead title="マイリスト一覧 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Grid container sx={styles.pageWrapper}>
        <SideBar />
        <Grid sx={styles.wrapper} ref={topRef}>
          <Grid container sx={styles.contentHeader}>
            <Grid container sx={styles.headerLeft}>
              <Typography sx={styles.headerTitle}>マイリスト一覧</Typography>
            </Grid>
            <Grid container sx={styles.headerRight}>
              <Button
                sx={styles.createButton}
                startIcon={<AddIcon sx={{ color: white }} />}
                onClick={() => handleOpenCreateModal()}
              >
                新規作成
              </Button>
            </Grid>
          </Grid>
          <Box sx={styles.materialList}>
            {showList &&
              lists.map((list, index) => (
                <Card
                  key={index}
                  sx={{
                    cursor: 'pointer',
                    boxShadow: 'unset',
                    borderRadius: 0,
                    color: 'unset',
                    width: '100%',
                    borderTop:
                      index === 0
                        ? `1px solid ${gray70}`
                        : `0px solid transparent`,
                    borderBottom: `1px solid ${gray70}`,
                    '@media screen and (max-width:850px)': {
                      borderTop: 'none',
                      borderBottom: 'none'
                    }
                  }}
                >
                  <CardContent sx={styles.cardContent}>
                    {/* 左側のラジオボタンエリア */}
                    <Grid
                      item
                      sx={{
                        backgroundColor: list.use_flg
                          ? primary10
                          : 'transparent',
                        width: '90px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '24px 24px 0 24px',
                        '@media screen and (max-width:850px)': {
                          display: 'none'
                        }
                      }}
                    >
                      <RadioGroup
                        value={list.use_flg ? list.id : ''}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleChangeUseMylist(e);
                        }}
                      >
                        <FormControlLabel
                          value={list.id}
                          control={<Radio />}
                          label=""
                          sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: 0,
                            marginRight: 0
                          }}
                        />
                        {list.use_flg && (
                          <Typography
                            sx={{ marginTop: '8px', fontSize: '14px' }}
                          >
                            利用中
                          </Typography>
                        )}
                      </RadioGroup>
                    </Grid>

                    {/* 右側の詳細表示エリア（常に表示） */}
                    <Grid item sx={{ width: '100%' }}>
                      <Card
                        variant="outlined"
                        sx={{
                          border: 'none',
                          boxShadow: 'none',
                          marginTop: '24px',
                          '@media screen and (max-width: 850px)': {
                            marginTop: '0px'
                          }
                        }}
                        onClick={(e) => {
                          // ラジオボタンをクリックした場合は遷移しない
                          if (
                            (e.target as Element).closest(
                              '.MuiFormControlLabel-root'
                            )
                          )
                            return;
                          navigate(`/mylist/${list.id}`);
                        }}
                      >
                        <CardContent
                          sx={{
                            padding: '0px'
                          }}
                        >
                          <Grid
                            mb={'16px'}
                            sx={{
                              '@media screen and (max-width: 850px)': {
                                display: 'none' // 850px以下では非表示
                              }
                            }}
                          >
                            <Typography
                              sx={[
                                styles.title,
                                {
                                  width: '100%',
                                  wordWrap: 'break-word',
                                  cursor: 'pointer',
                                  ':hover': {
                                    textDecoration: 'underline'
                                  }
                                }
                              ]}
                              onClick={() => navigate(`/mylist/${list.id}`)}
                            >
                              {list.title}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            sx={{
                              display: 'none', // PC時は非表示
                              '@media screen and (max-width: 850px)': {
                                backgroundColor: list.use_flg
                                  ? primary10
                                  : 'transparent',
                                display: 'flex', // 850px以下では表示
                                alignItems: 'center',
                                borderTop: '1px solid #ccc',
                                padding: '16px 12px 16px 0px',
                                justifyContent: 'space-between'
                              }
                            }}
                          >
                            <RadioGroup
                              value={list.use_flg ? list.id : ''}
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center'
                              }}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleChangeUseMylist(e);
                              }}
                            >
                              <FormControlLabel
                                value={list.id}
                                control={<Radio />}
                                label=""
                                sx={{
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginLeft: 0,
                                  marginRight: 0
                                }}
                              />
                              <Typography
                                sx={styles.title}
                                onClick={() => navigate(`/mylist/${list.id}`)}
                              >
                                {list.title}
                              </Typography>
                            </RadioGroup>
                            {list.use_flg && <Typography>利用中</Typography>}
                          </Grid>

                          {/* テーブル */}
                          <Box sx={styles.table}>
                            {/* １行目 */}
                            <Grid container>
                              <Grid item xs={12} md={6} sx={styles.row}>
                                <Grid
                                  item
                                  xs={3}
                                  sx={[styles.item, styles.cellText]}
                                >
                                  登録日
                                </Grid>
                                <Grid
                                  item
                                  xs={9}
                                  sx={[styles.cell, styles.cellText]}
                                >
                                  {convertDateFormat(list.created_at)}
                                </Grid>
                              </Grid>
                              <Grid item xs={12} md={6} sx={styles.row}>
                                <Grid
                                  item
                                  xs={3}
                                  sx={[styles.item, styles.cellText]}
                                >
                                  更新日
                                </Grid>
                                <Grid
                                  item
                                  xs={9}
                                  sx={[styles.cell, styles.cellText]}
                                >
                                  {convertDateFormat(list.updated_at)}
                                </Grid>
                              </Grid>
                            </Grid>
                            {/* ２行目 */}
                            <Grid container>
                              <Grid item xs={12} md={6} sx={styles.row}>
                                <Grid
                                  item
                                  xs={3}
                                  sx={[styles.item, styles.cellText]}
                                >
                                  追加建材数
                                </Grid>
                                <Grid
                                  item
                                  xs={9}
                                  sx={[styles.cell, styles.cellText]}
                                >
                                  {list.product_count}
                                </Grid>
                              </Grid>
                              <Grid item xs={12} md={6} sx={styles.row}>
                                <Grid
                                  item
                                  xs={3}
                                  sx={[styles.item, styles.cellText]}
                                >
                                  更新者
                                </Grid>
                                <Grid
                                  item
                                  xs={9}
                                  sx={[styles.cell, styles.cellText]}
                                >
                                  {list.updated_by}
                                </Grid>
                              </Grid>
                            </Grid>
                            {/* ４行目 */}
                            <Grid container>
                              <Grid item xs={12} sx={styles.row}>
                                <Grid
                                  item
                                  xs={3}
                                  md={1.5}
                                  sx={[styles.item, styles.cellText]}
                                >
                                  備考１
                                </Grid>
                                <Grid
                                  item
                                  xs={9}
                                  md={10.5}
                                  sx={[styles.cell, styles.cellText]}
                                >
                                  {list.remarks_first}
                                </Grid>
                              </Grid>
                            </Grid>
                            {/* ５行目 */}
                            <Grid container>
                              <Grid
                                item
                                xs={12}
                                sx={[styles.row, { border: 'none' }]}
                              >
                                <Grid
                                  item
                                  xs={3}
                                  md={1.5}
                                  sx={[styles.item, styles.cellText]}
                                >
                                  備考２
                                </Grid>
                                <Grid
                                  item
                                  xs={9}
                                  md={10.5}
                                  sx={[styles.cell, styles.cellText]}
                                >
                                  {list.remarks_second}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* ボタンセクション */}
                          <Box mt={3} display="flex" justifyContent="flex-end">
                            <Button
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenEditModal({
                                  title: list.title,
                                  remarks1: list.remarks_first,
                                  remarks2: list.remarks_second
                                });
                                setFocusMylist({
                                  id: list.id,
                                  title: list.title,
                                  remarks1: list.remarks_first,
                                  remarks2: list.remarks_second
                                });
                              }}
                              startIcon={
                                <CreateOutlined sx={{ color: primaryColor }} />
                              }
                              sx={[
                                primaryButtonOutlinedColor,
                                styles.editButton
                              ]}
                              variant="outlined"
                            >
                              編集する
                            </Button>

                            <Button
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenCopyModal({
                                  title: list.title,
                                  remarks1: list.remarks_first,
                                  remarks2: list.remarks_second
                                });
                                setFocusMylist({
                                  id: list.id,
                                  title: list.title,
                                  remarks1: list.remarks_first,
                                  remarks2: list.remarks_second
                                });
                              }}
                              startIcon={
                                <FileCopyIcon sx={{ color: primaryColor }} />
                              }
                              sx={[
                                primaryButtonOutlinedColor,
                                styles.copyButton
                              ]}
                              variant="outlined"
                            >
                              コピーする
                            </Button>

                            <Button
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenDeleteModal();
                                setFocusMylist({
                                  id: list.id,
                                  title: list.title,
                                  remarks1: list.remarks_first,
                                  remarks2: list.remarks_second
                                });
                              }}
                              startIcon={
                                <DeleteOutline sx={{ color: gray90 }} />
                              }
                              sx={[
                                tertiaryButtonOutlinedColor,
                                styles.deleteButton
                              ]}
                              variant="outlined"
                            >
                              削除する
                            </Button>
                            <DeleteOutline
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenDeleteModal();
                                setFocusMylist({
                                  id: list.id,
                                  title: list.title,
                                  remarks1: list.remarks_first,
                                  remarks2: list.remarks_second
                                });
                              }}
                              sx={styles.deleteIcon}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            {totalPages > 1 ? (
              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            ) : null}
          </Box>
        </Grid>
      </Grid>

      {/* マイリスト作成モーダル */}
      <Modal
        open={openCreateModal}
        onClose={() => {
          setOpenCreateModal(false);
          reset();
        }}
        sx={{ m: '16px' }}
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              width: '24px',
              height: '24px',
              color: gray90
            }}
          >
            <Close
              onClick={() => {
                setOpenCreateModal(false);
                reset();
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '22px',
              fontWeight: 500,
              color: gray100,
              mb: '24px'
            }}
          >
            新規作成
          </Typography>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText="タイトル"
                requiredText="必須"
                placeholder="マイリスト"
                formMb="24px"
                width="100%"
                error={Boolean(errors.title)}
                helperText={errors.title?.message}
              />
            )}
          />
          <Controller
            name="remarks1"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText="備考１"
                required
                formMb="24px"
                width="100%"
                multiline
                fieldType="textareaRow3"
                rows={3}
                error={Boolean(errors.remarks1)}
                helperText={errors.remarks1?.message}
              />
            )}
          />
          <Controller
            name="remarks2"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText="備考２"
                required
                formMb="24px"
                width="100%"
                multiline
                fieldType="textareaRow3"
                rows={3}
                error={Boolean(errors.remarks2)}
                helperText={errors.remarks2?.message}
              />
            )}
          />
          <Box sx={{ mt: '40px' }}>
            <Button
              disabled={buttonDisabledFlag()}
              sx={[primaryButtonBackgroundColor, { width: '100%' }]}
              onClick={async () => {
                setFirstSubmit(true);
                await handleSubmit(async () => {
                  await handleCreateMylist();
                  setOpenCreateModal(false);
                })();
              }}
            >
              <Typography>作成する</Typography>
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* マイリスト編集モーダル */}
      <Modal
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setFocusMylist(null);
        }}
        sx={{ m: '16px' }}
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              width: '24px',
              height: '24px',
              color: gray90
            }}
          >
            <Close
              onClick={() => {
                setOpenEditModal(false);
                setFocusMylist(null);
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '22px',
              fontWeight: 500,
              color: gray100,
              mb: '24px'
            }}
          >
            編集
          </Typography>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText="タイトル"
                requiredText="必須"
                formMb="24px"
                width="100%"
                error={Boolean(errors.title)}
                helperText={errors.title?.message}
              />
            )}
          />
          <Controller
            name="remarks1"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText="備考１"
                required
                formMb="24px"
                width="100%"
                multiline
                fieldType="textareaRow3"
                rows={3}
                error={Boolean(errors.remarks1)}
                helperText={errors.remarks1?.message}
              />
            )}
          />
          <Controller
            name="remarks2"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText="備考２"
                required
                formMb="24px"
                width="100%"
                multiline
                fieldType="textareaRow3"
                rows={3}
                error={Boolean(errors.remarks2)}
                helperText={errors.remarks2?.message}
              />
            )}
          />
          <Box sx={{ mt: '40px' }}>
            <Button
              disabled={buttonDisabledFlag()}
              sx={[primaryButtonBackgroundColor, { width: '100%' }]}
              onClick={async () => {
                setFirstSubmit(true);
                await handleSubmit(async () => {
                  handleEditMylist();
                  setOpenEditModal(false);
                })();
              }}
            >
              <Typography>保存する</Typography>
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* マイリストコピーモーダル */}
      <Modal
        open={openCopyModal}
        onClose={() => {
          setOpenCopyModal(false);
          setFocusMylist(null);
        }}
        sx={{ m: '16px' }}
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              width: '24px',
              height: '24px',
              color: gray90
            }}
          >
            <Close
              onClick={() => {
                setOpenCopyModal(false);
                setFocusMylist(null);
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '22px',
              fontWeight: 500,
              color: gray100,
              mb: '24px'
            }}
          >
            コピーを作成
          </Typography>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText="タイトル"
                requiredText="必須"
                placeholder="マイリスト"
                formMb="24px"
                width="100%"
                error={Boolean(errors.title)}
                helperText={errors.title?.message}
              />
            )}
          />
          <Controller
            name="remarks1"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText="備考１"
                required
                formMb="24px"
                width="100%"
                multiline
                fieldType="textareaRow3"
                rows={3}
                error={Boolean(errors.remarks1)}
                helperText={errors.remarks1?.message}
              />
            )}
          />
          <Controller
            name="remarks2"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText="備考２"
                required
                formMb="24px"
                width="100%"
                multiline
                fieldType="textareaRow3"
                rows={3}
                error={Boolean(errors.remarks2)}
                helperText={errors.remarks2?.message}
              />
            )}
          />
          <Box sx={{ mt: '40px' }}>
            <Button
              disabled={buttonDisabledFlag()}
              sx={[primaryButtonBackgroundColor, { width: '100%' }]}
              onClick={async () => {
                setFirstSubmit(true);
                await handleSubmit(async () => {
                  handleCopyMylist();
                  setOpenCopyModal(false);
                })();
              }}
            >
              <Typography>保存する</Typography>
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* マイリスト削除モーダル */}
      <Modal
        open={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
          setFocusMylist(null);
        }}
        sx={{ m: '16px' }}
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              width: '24px',
              height: '24px',
              color: gray90
            }}
          >
            <Close
              onClick={() => {
                setOpenDeleteModal(false);
                setFocusMylist(null);
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '22px',
              fontWeight: 500,
              color: gray100,
              mb: '24px'
            }}
          >
            マイリストから削除
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              color: gray100,
              mb: '40px'
            }}
          >
            削除すると元に戻すことはできません。よろしいですか？
          </Typography>
          <Grid container sx={{ width: '100%' }} justifyContent="space-between">
            <Button
              fullWidth
              variant="outlined"
              sx={{
                ...tertiaryButtonOutlinedColor,
                fontSize: '16px',
                height: '43px',
                width: '50%',
                maxWidth: '248px',
                '@media screen and (max-width:600px)': {
                  maxWidth: '140px'
                }
              }}
              onClick={() => {
                setOpenDeleteModal(false);
                setFocusMylist(null);
              }}
            >
              キャンセル
            </Button>
            <Button
              fullWidth
              variant="contained"
              sx={{
                ...primaryButtonBackgroundColor,
                fontSize: '16px',
                height: '43px',
                width: '50%',
                maxWidth: '248px',
                '@media screen and (max-width:600px)': {
                  maxWidth: '140px'
                }
              }}
              onClick={() => {
                handleDeleteMylist();
                setOpenDeleteModal(false);
              }}
            >
              削除する
            </Button>
          </Grid>
        </Box>
      </Modal>

      <CustomSnackbar
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />
    </>
  );
};

export default MyListList;
